import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Plus, Trash2, Key, Shield, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', password: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const token = localStorage.getItem('superAdminToken');
  const currentUser = JSON.parse(localStorage.getItem('superAdminUser') || '{}');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/super-admins`, { headers: getHeaders() });
      setAdmins(res.data);
    } catch { toast.error('Failed to load admins'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const addAdmin = async () => {
    if (!newAdmin.email || !newAdmin.name || !newAdmin.password) { toast.error('All fields required'); return; }
    if (newAdmin.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/platform/super-admins`, newAdmin, { headers: getHeaders() });
      toast.success(`Admin "${newAdmin.name}" added`);
      setAddOpen(false);
      setNewAdmin({ email: '', name: '', password: '' });
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add admin'); }
    setSaving(false);
  };

  const removeAdmin = async (id, name) => {
    if (!window.confirm(`Remove super admin "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/platform/super-admins/${id}`, { headers: getHeaders() });
      toast.success(`Admin "${name}" removed`);
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove admin'); }
  };

  const changePassword = async () => {
    if (!passwords.current_password || !passwords.new_password) { toast.error('All fields required'); return; }
    if (passwords.new_password !== passwords.confirm) { toast.error('Passwords do not match'); return; }
    if (passwords.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/platform/super-admins/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, { headers: getHeaders() });
      toast.success('Password changed successfully');
      setPwdOpen(false);
      setPasswords({ current_password: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Management</h1>
          <p className="text-slate-400 mt-1">Manage super admin accounts and credentials</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:text-white" data-testid="change-password-btn">
                <Key size={14} className="mr-2" />Change Password
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader><DialogTitle className="text-white">Change Your Password</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Current Password</Label>
                  <Input type="password" value={passwords.current_password} onChange={e => setPasswords(p => ({ ...p, current_password: e.target.value }))} className="mt-1 bg-slate-800 border-slate-600 text-white" data-testid="current-password" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">New Password</Label>
                  <Input type="password" value={passwords.new_password} onChange={e => setPasswords(p => ({ ...p, new_password: e.target.value }))} className="mt-1 bg-slate-800 border-slate-600 text-white" data-testid="new-password" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Confirm New Password</Label>
                  <Input type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} className="mt-1 bg-slate-800 border-slate-600 text-white" data-testid="confirm-password" />
                </div>
                <Button onClick={changePassword} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold" data-testid="save-password-btn">
                  {saving ? 'Saving...' : 'Update Password'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white font-bold" data-testid="add-admin-btn">
                <Plus size={14} className="mr-2" />Add Admin
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader><DialogTitle className="text-white">Add Super Admin</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Full Name</Label>
                  <Input value={newAdmin.name} onChange={e => setNewAdmin(a => ({ ...a, name: e.target.value }))} className="mt-1 bg-slate-800 border-slate-600 text-white" placeholder="John Smith" data-testid="new-admin-name" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Email Address</Label>
                  <Input type="email" value={newAdmin.email} onChange={e => setNewAdmin(a => ({ ...a, email: e.target.value }))} className="mt-1 bg-slate-800 border-slate-600 text-white" placeholder="admin@recruitizr.com" data-testid="new-admin-email" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Password</Label>
                  <Input type="password" value={newAdmin.password} onChange={e => setNewAdmin(a => ({ ...a, password: e.target.value }))} className="mt-1 bg-slate-800 border-slate-600 text-white" placeholder="Min 8 characters" data-testid="new-admin-password" />
                </div>
                <Button onClick={addAdmin} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="confirm-add-admin-btn">
                  {saving ? 'Adding...' : 'Add Super Admin'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Admin List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2"><Users size={18} />Super Admins ({admins.length})</CardTitle>
          <CardDescription className="text-slate-400">Users with full platform management access</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400 font-bold">Admin</TableHead>
                <TableHead className="text-slate-400 font-bold">Email</TableHead>
                <TableHead className="text-slate-400 font-bold">Created</TableHead>
                <TableHead className="text-slate-400 font-bold">Added By</TableHead>
                <TableHead className="text-slate-400 font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map(a => (
                <TableRow key={a.id} className="border-slate-700 hover:bg-slate-800/50" data-testid={`admin-row-${a.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold text-sm">
                        {(a.name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{a.name}</p>
                        {a.id === currentUser.id && <span className="text-[10px] text-violet-400 font-bold uppercase">(You)</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">{a.email}</TableCell>
                  <TableCell className="text-slate-400 text-xs">{a.created_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-slate-400 text-xs">{a.created_by || 'System'}</TableCell>
                  <TableCell>
                    {a.id !== currentUser.id ? (
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7" onClick={() => removeAdmin(a.id, a.name)} data-testid={`remove-admin-${a.id}`}>
                        <Trash2 size={12} className="mr-1" />Remove
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
